import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { CoachModal } from '@/features/coach/components/CoachModal';
import type { FeedbackDraftScope } from '@/lib/types';
import { useFeedbackAuthoringScope } from '../hooks/useFeedbackAuthoringScope';
import { useFeedbackDrafts } from '../hooks/useFeedbackDrafts';
import { FeedbackContinuationResume, type FeedbackContinuationTarget } from './FeedbackContinuationResume';
import { feedbackMessageExcerpt } from '../feedbackContent';
import { useComposerDraftContext } from '@/features/coach-review/composerDrafts';
import { listFeedbackContinuations } from '@/lib/feedbackAuthoringContinuation';
import type { FeedbackAuthoringDraftItem } from '@/lib/types';

export type FeedbackDraftResumeProps = {
  scope: FeedbackDraftScope; active?: boolean; onClose: () => void;
  onResumeContinuation?: (target: FeedbackContinuationTarget) => void;
  onResume: (feedbackId: string) => void; onNew: () => void;
};

/** Server discovery unifies both persisted forms; pending operations stay explicit. */
export function FeedbackDraftResume(props: FeedbackDraftResumeProps) {
  return <ResumeEntry key={JSON.stringify(props.scope)} {...props} />;
}

function ResumeEntry({ scope, active = true, onClose, onResume, onNew, onResumeContinuation }: FeedbackDraftResumeProps) {
  const authorized = useFeedbackAuthoringScope(scope);
  const context = useComposerDraftContext();
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string>();
  const [resumeError, setResumeError] = useState<string>();
  const query = useFeedbackDrafts(scope, authorized && active, search, cursor);
  function resumeRow(row: FeedbackAuthoringDraftItem) {
    if (!authorized || !context) return;
    // Prefer the original mounted entry/journal over recreating a saved form.
    // This retains unsaved typing and unresolved commands in the current tab.
    let kept: ReturnType<typeof listFeedbackContinuations>;
    try { kept = listFeedbackContinuations(context); } catch {
      setResumeError('Draft recovery could not be checked in this browser. Your saved drafts are unchanged.'); return;
    }
    const match = kept.find(entry => row.workspace_id ? entry.workspace_id === row.workspace_id : entry.feedback_id === row.feedback_id);
    if (onResumeContinuation && (row.workspace_id || match)) onResumeContinuation({
      entryId: match?.entry_id ?? crypto.randomUUID(), workspaceId: row.workspace_id ?? match?.workspace_id ?? undefined,
      draftId: row.feedback_id ?? match?.feedback_id ?? undefined });
    else if (row.kind === 'draft' && row.feedback_id) onResume(row.feedback_id);
    else setResumeError('This draft cannot be opened here. Close and open Give feedback again.');
  }
  return <CoachModal active={active} title="Give feedback" onClose={onClose}
    footer={<Button onClick={onNew} disabled={!authorized}>New Feedback</Button>}>
    {!authorized ? <p role="alert">Your coaching context changed. Close and open Feedback again.</p> : <div className="space-y-4">
      {onResumeContinuation && <FeedbackContinuationResume scope={scope} active={active} recoveryOnly
        discovered={!query.isFetching && !query.loadError ? query.data?.items : undefined} onResume={onResumeContinuation} />}
      <h3 className="font-display text-lg">Drafts</h3>
      <p className="text-sm text-ink-muted">Resume a saved draft or start new Feedback. Drafts are private to you.</p>
      {resumeError && <p role="alert">{resumeError}</p>}
      {((query.data?.authorized_total ?? 0) > 5 || search) && <label className="block text-sm font-semibold">Search drafts<input type="search" value={search}
        onChange={event => { setSearch(event.target.value); setCursor(undefined); }}
        className="mt-1 block w-full rounded-control border border-rule bg-canvas p-2 font-normal focus:outline-none focus:ring-2 focus:ring-accent" /></label>}
      {query.loadError ? <div role="alert"><p>Saved drafts could not be loaded.</p>
        <Button variant="outline" onClick={() => { if (cursor) { query.restart(); setCursor(undefined); } else query.retry(); }}>Try again</Button></div>
        : query.isFetching ? <p role="status">Loading drafts…</p>
          : query.data && <>
            <p className="text-xs text-ink-muted">{query.data.total === 0 ? 'No saved drafts found.'
              : `${query.data.range_start}–${query.data.range_end} of ${query.data.total} drafts`}</p>
            <ul className="divide-y divide-rule">{query.data.items.map(row =>
              <li key={row.row_id} className="flex items-center gap-3 py-3">
                <div className="min-w-0 flex-1"><p className="break-words font-semibold text-ink">{feedbackMessageExcerpt(row.message)}</p>
                  <p className="text-xs text-ink-muted">{row.publication === 'published' ? `Changes to published Feedback · version ${row.base_version}` : 'New Feedback draft'}</p>
                  {row.publication === 'published' && row.current_feedback_version !== row.base_version && <p className="text-xs text-ink-muted">Published version {row.current_feedback_version} is newer.</p>}
                  {row.publication === 'published' && <p className="text-xs text-ink-muted">Published Feedback stays unchanged.</p>}</div>
                <Button variant="outline" onClick={() => resumeRow(row)} aria-label={`Resume ${feedbackMessageExcerpt(row.message)}`}>Resume</Button>
              </li>)}</ul>
            <div className="flex gap-2">{cursor && <Button variant="ghost" onClick={() => setCursor(undefined)}>First page</Button>}
              {query.data.next_cursor && <Button variant="outline" onClick={() => setCursor(query.data!.next_cursor!)}>Next page</Button>}</div>
          </>}
    </div>}
  </CoachModal>;
}
